import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 400;
const MARGIN = { top: 20, right: 20, bottom: 50, left: 60 };

const COLORS = ['#2563eb', '#dc2626', '#16a34a', '#f59e0b', '#8b5cf6'];

const FIELD_LABELS = {
  pest365: 'Pest365',
  pest365Plus: 'Pest365+',
  oneTime: 'One-Time',
  initialServiceVisit: 'Init. Visit',
  mosquitoTickControl: 'Mosquito/Tick',
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  legendGini: {
    fontSize: '11px',
    color: '#9ca3af',
    fontWeight: 400,
  },
  container: {
    overflowX: 'auto',
  },
  emptyState: {
    color: '#6b7280',
    textAlign: 'center',
    padding: '40px 0',
    fontSize: '14px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function LorenzCurveChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.fields || data.fields.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(400, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // -- Scales --
    const x = d3.scaleLinear().domain([0, 1]).range([0, innerW]);
    const y = d3.scaleLinear().domain([0, 1]).range([innerH, 0]);

    // -- Grid lines --
    const gridTicks = [0.2, 0.4, 0.6, 0.8];
    g.selectAll('.grid-h')
      .data(gridTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    g.selectAll('.grid-v')
      .data(gridTicks)
      .enter()
      .append('line')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // -- Line of Equality (diagonal) --
    g.append('line')
      .attr('x1', x(0))
      .attr('y1', y(0))
      .attr('x2', x(1))
      .attr('y2', y(1))
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,4');

    // -- Draw each field's Lorenz curve --
    data.fields.forEach((field, idx) => {
      const color = COLORS[idx % COLORS.length];
      const points = field.points || [];
      if (points.length < 2) return;

      // Shaded area between equality line and Lorenz curve
      const areaGen = d3.area()
        .x((d) => x(d.x))
        .y0((d) => y(d.x)) // equality line: y = x
        .y1((d) => y(d.y)) // Lorenz curve value
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(points)
        .attr('d', areaGen)
        .attr('fill', color)
        .attr('fill-opacity', 0.08)
        .attr('stroke', 'none');

      // Lorenz curve line
      const lineGen = d3.line()
        .x((d) => x(d.x))
        .y((d) => y(d.y))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(points)
        .attr('d', lineGen)
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round');
    });

    // -- Axes --

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(5)
          .tickSize(0)
          .tickFormat((v) => `${(v * 100).toFixed(0)}%`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(5)
          .tickSize(0)
          .tickFormat((v) => `${(v * 100).toFixed(0)}%`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Axis labels
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 40)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Cumulative % of Addresses');

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Cumulative % of Total Pricing');

    // -- "Line of Equality" label --
    g.append('text')
      .attr('x', x(0.52))
      .attr('y', y(0.56))
      .attr('font-size', '9px')
      .attr('fill', '#9ca3af')
      .attr('transform', `rotate(-${Math.atan(innerH / innerW) * (180 / Math.PI)}, ${x(0.52)}, ${y(0.56)})`)
      .text('Line of Equality');

    // -- Tooltip hover overlay --
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Hover dots for each field
    const hoverDots = data.fields.map((field, idx) => {
      return g.append('circle')
        .attr('r', 4)
        .attr('fill', COLORS[idx % COLORS.length])
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
        .style('display', 'none');
    });

    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const xVal = x.invert(mx);
        const clampedX = Math.max(0, Math.min(1, xVal));

        crosshair
          .attr('x1', x(clampedX))
          .attr('x2', x(clampedX))
          .style('display', null);

        const fieldValues = data.fields.map((field, idx) => {
          const points = field.points || [];
          if (points.length === 0) return null;

          // Find closest point
          const bisect = d3.bisector((d) => d.x).left;
          let i = bisect(points, clampedX);
          if (i > 0 && i < points.length) {
            const d0 = points[i - 1];
            const d1 = points[i];
            i = (clampedX - d0.x) > (d1.x - clampedX) ? i : i - 1;
          }
          i = Math.max(0, Math.min(points.length - 1, i));

          const pt = points[i];

          // Position hover dot
          hoverDots[idx]
            .attr('cx', x(pt.x))
            .attr('cy', y(pt.y))
            .style('display', null);

          return {
            field: field.field,
            label: FIELD_LABELS[field.field] || field.field,
            color: COLORS[idx % COLORS.length],
            x: pt.x,
            y: pt.y,
          };
        }).filter(Boolean);

        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          fields: fieldValues,
        });
      })
      .on('mouseleave', () => {
        crosshair.style('display', 'none');
        hoverDots.forEach((dot) => dot.style('display', 'none'));
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Handle window resize
  useEffect(() => {
    if (!data || !data.fields || data.fields.length === 0) return;

    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        setTooltip(null);
      }, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [data]);

  // Empty state
  if (!data || !data.fields || data.fields.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Price Inequality (Lorenz Curve)</div>
        <div style={styles.subtitle}>
          Cumulative distribution of pricing across addresses
        </div>
        <div style={styles.emptyState}>No Lorenz curve data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.title}>Price Inequality (Lorenz Curve)</div>
      <div style={styles.subtitle}>
        Cumulative distribution of pricing across addresses — closer to the diagonal means more equal pricing
      </div>

      {/* Legend with Gini coefficients */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#9ca3af" strokeWidth="1.5" strokeDasharray="6,4" />
          </svg>
          <span>Line of Equality</span>
        </div>
        {data.fields.map((field, idx) => {
          const color = COLORS[idx % COLORS.length];
          const label = FIELD_LABELS[field.field] || field.field;
          return (
            <div key={field.field} style={styles.legendItem}>
              <svg width="20" height="10">
                <line x1="0" y1="5" x2="20" y2="5" stroke={color} strokeWidth="2" />
              </svg>
              <span>{label}</span>
              {field.gini != null && (
                <span style={styles.legendGini}>
                  (Gini: {field.gini.toFixed(3)})
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            Addresses: {((tooltip.fields[0]?.x || 0) * 100).toFixed(1)}%
          </div>
          {tooltip.fields.map((f) => (
            <div
              key={f.field}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '2px',
              }}
            >
              <span style={{ color: f.color, fontWeight: 500 }}>{f.label}</span>
              <span style={{ fontWeight: 600, color: '#374151' }}>
                {(f.y * 100).toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
